import React from 'react';
import { useReactiveVar } from '@apollo/client';
import { addBreadcrumb } from '@sentry/react';

import { useCustomerLogoutMutation } from '@ocx/graphql';
import { sentrySetUser } from '@ocx/data-membership';
import { InMemoryStorage } from '@ocx/data-in-memory-storage';
import { useConfigurationContext } from '@ocx/data-configuration';

import { clearCache } from '../cache-layer/utils';
import { authService } from './instances/authService';
import { isLoggedInVar } from './authReactiveVariables';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';

interface IUseLogoutControllerReturns {
  isLoggedIn: boolean;
  logout(params?: { skipMutation?: boolean }): Promise<void>;
}

interface IUseLogoutControllerParams {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?(error: Error): any;
}

export const useLogoutController = (params?: IUseLogoutControllerParams): IUseLogoutControllerReturns => {
  const isLoggedIn = useReactiveVar(isLoggedInVar);
  const [customerLogout] = useCustomerLogoutMutation();
  const { setMembershipOverrides } = useConfigurationContext();

  const { reset: resetRudderStack } = useRudderStack();

  const logout = React.useCallback<IUseLogoutControllerReturns['logout']>(
    async ({ skipMutation } = {}) => {
      try {
        setMembershipOverrides(null);
        if (!skipMutation) {
          await customerLogout();
        }

        sentrySetUser(null);
        resetRudderStack();

        await authService.clearSession();
        await clearCache();
        InMemoryStorage.eraseAll();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        addBreadcrumb({ message: 'Failed to log out', data: { error: e } });
        params?.onError?.(e);
      }
    },
    [setMembershipOverrides, resetRudderStack, customerLogout, params],
  );

  return {
    isLoggedIn,
    logout,
  };
};
